// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { Modal } from '@carbon/react';
import { useSessionGuardStore } from '../../stores/sessionGuardStore';
import './IdleWarningModal.css';

interface IdleWarningModalProps {
  secondsLeft: number;
}

function formatCountdown(seconds: number): string {
  const mm = Math.floor(seconds / 60);
  const ss = seconds % 60;
  return `${mm}:${String(ss).padStart(2, '0')}`;
}

/**
 * Shown after 45 min idle, with a 2-min countdown to auto-lock. A Carbon
 * `Modal` (transactional, not `passiveModal`) so it matches the rest of the
 * app's modal styling exactly, rather than a hand-rolled approximation of
 * it. `onRequestClose` is a no-op — Carbon's non-passive modals already
 * ignore an outside click by default, but Escape and the header's close
 * icon both still call `onRequestClose` unconditionally, so a no-op is what
 * makes the modal dismissable *only* by its own "Stay signed in" button
 * (`.idle-warning-modal-close` in `IdleWarningModal.css` also hides that
 * close icon — a visible-but-disabled affordance would be its own kind of
 * confusing).
 */
function IdleWarningModal({ secondsLeft }: IdleWarningModalProps) {
  const handleStaySignedIn = () => {
    const store = useSessionGuardStore.getState();
    store.dismissWarning();
    store.recordActivity();
  };

  return (
    <Modal
      open
      size="sm"
      className="idle-warning-modal"
      modalHeading="Still there?"
      primaryButtonText="Stay signed in"
      onRequestSubmit={handleStaySignedIn}
      onRequestClose={() => {}}
      preventCloseOnClickOutside
    >
      <p>
        You&apos;ve been inactive. For your security, you&apos;ll be signed out in{' '}
        <strong className="idle-warning-countdown">{formatCountdown(secondsLeft)}</strong>.
      </p>
    </Modal>
  );
}

export default IdleWarningModal;
